import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { TabPanel, TabView } from "primereact/tabview";
import { ETypeOfNotification, setNotification } from "medere-ui-framework";
import { BudgetDTO } from "../../../dto/budget/BudgetDTO";
import { BudgetDetailDTO, HospitalizationDaysBudgetDetailDTO, ProfessionalActivityBudgetDetailDTO } from "../../../dto/budget/BudgetDetailDTO";
import { BILL_ITEM_CATEGORIES, BILL_ITEM_CATEGORY_CONFIG, BillItemCategory, getDuplicatedHealthPatientServices, getItemWarnings } from "./billItems";
import { ContractContext } from "./contractRevaluation";
import HospitalizationDaysTab from "./HospitalizationDaysTab";
import ItemDetailDrawer from "./ItemDetailDrawer";
import ProfessionalActivityTab from "./ProfessionalActivityTab";
import SupplyTab, { SupplyCategory } from "./SupplyTab";

interface BillItemsSectionProps {
    budget: BudgetDTO;
    setBudget: Dispatch<SetStateAction<BudgetDTO>>;
    contract: ContractContext;
    healthEntity: number | null | undefined;
    hospitalizationId: number | null | undefined;
    /** Acciones de la sección (traer ítems del período), a la derecha del título. */
    actions: ReactNode;
}

/** Sección "Ítems": una pestaña por categoría y el panel "Editar …" de cada ítem. */
export default function BillItemsSection({ budget, setBudget, contract, healthEntity, hospitalizationId, actions }: BillItemsSectionProps) {
    const dispatch = useDispatch();
    const [activeIndex, setActiveIndex] = useState(0);
    const [editing, setEditing] = useState<{ category: BillItemCategory; index: number } | null>(null);

    const items = (category: BillItemCategory) => (budget[category] ?? []) as BudgetDetailDTO[];
    const setItems = (category: BillItemCategory, next: BudgetDetailDTO[]) =>
        setBudget(prev => ({ ...prev, [category]: next }));

    const notify = (title: string) =>
        dispatch(setNotification({ title, icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));

    /** Cantidad de ítems con avisos por categoría, para marcar la pestaña. */
    const warningsByCategory = useMemo(() => {
        const result = {} as Record<BillItemCategory, number>;
        BILL_ITEM_CATEGORIES.forEach(category => {
            const list = (budget[category] ?? []) as BudgetDetailDTO[];
            const duplicated = getDuplicatedHealthPatientServices(list);
            result[category] = list.filter(item => getItemWarnings(category, item, duplicated).length > 0).length;
        });
        return result;
    }, [budget]);

    const tabHeader = (category: BillItemCategory) => {
        const warnings = warningsByCategory[category];
        return (
            <span className="nv-tab-header">
                {BILL_ITEM_CATEGORY_CONFIG[category].title}
                <span className="nv-tab-count">{items(category).length}</span>
                {warnings > 0 && (
                    <i
                        className="pi pi-exclamation-triangle nv-tab-warning nv-tip"
                        role="img"
                        aria-label={`${warnings} para revisar`}
                        data-pr-tooltip={warnings === 1 ? '1 ítem para revisar' : `${warnings} ítems para revisar`}
                    />
                )}
            </span>
        );
    };

    const renderTab = (category: BillItemCategory) => {
        const onEdit = (index: number) => setEditing({ category, index });
        switch (category) {
            case 'professionalActivities':
                return (
                    <ProfessionalActivityTab
                        items={budget.professionalActivities}
                        onChange={(next: ProfessionalActivityBudgetDetailDTO[]) => setItems(category, next)}
                        onEdit={onEdit}
                        contract={contract}
                        healthEntity={healthEntity}
                    />
                );
            case 'hospitalizationDays':
                return (
                    <HospitalizationDaysTab
                        items={budget.hospitalizationDays}
                        onChange={(next: HospitalizationDaysBudgetDetailDTO[]) => setItems(category, next)}
                        onEdit={onEdit}
                        contract={contract}
                        healthEntity={healthEntity}
                        hospitalizationId={hospitalizationId}
                    />
                );
            default:
                return (
                    <SupplyTab
                        category={category as SupplyCategory}
                        items={items(category)}
                        onChange={(next) => setItems(category, next)}
                        onEdit={onEdit}
                    />
                );
        }
    };

    const editingItem = editing ? items(editing.category)[editing.index] : undefined;

    return (
        <section className="nv-section" aria-labelledby="nv-items-title">
            <div className="nv-section-head">
                <h2 className="nv-section-title" id="nv-items-title">Ítems</h2>
                <div className="nv-row-8">{actions}</div>
            </div>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="nv-tabs">
                {BILL_ITEM_CATEGORIES.map(category => (
                    <TabPanel key={category} header={tabHeader(category)}>
                        <div className="nv-card">{renderTab(category)}</div>
                    </TabPanel>
                ))}
            </TabView>

            {editing && editingItem && (
                <ItemDetailDrawer
                    key={`${editing.category}-${editing.index}`}
                    category={editing.category}
                    item={editingItem}
                    onHide={() => setEditing(null)}
                    onApply={(item) => {
                        setItems(editing.category, items(editing.category).map((current, i) => i === editing.index ? item : current));
                        setEditing(null);
                        notify('Cambios aplicados');
                    }}
                    onAppliedToAll={(units) => notify(units === 1 ? 'Distribución copiada a 1 unidad' : `Distribución copiada a ${units} unidades`)}
                />
            )}
        </section>
    );
}
